import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  inject,
  input,
} from '@angular/core';
import { SoftSlotDirective } from '@softline/ui-core';
import { IconComponent } from '@softline/ui-core';
import { NumberBadgeComponent } from '@softline/ui-core';
import { TABS_HOST } from '../tabs-host';

@Component({
  selector: 'soft-ds-tab',
  imports: [NgTemplateOutlet, IconComponent, NumberBadgeComponent],
  templateUrl: './tab.component.html',
  styleUrls: ['./tab.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    role: 'tab',
    '[class]': 'hostClasses()',
    '[class.is-active]': 'isActive()',
    '[class.is-disabled]': 'disabled()',
    '[attr.aria-selected]': 'isActive() ? "true" : "false"',
    '[attr.aria-disabled]': 'disabled() ? "true" : null',
    '[attr.tabindex]': 'isActive() && !disabled() ? 0 : -1',
    '(click)': 'onClick()',
    '(keydown)': 'onKeydown($event)',
  },
})
export class DsTabComponent<T = unknown> {
  private readonly host = inject(TABS_HOST);

  readonly value = input.required<T>();
  readonly title = input<string | null>(null);
  readonly disabled = input<boolean, unknown>(false, {
    transform: booleanAttribute,
  });
  readonly icon = input<string | null>(null);
  readonly badge = input<string | number | null>(null);

  private readonly slots = contentChildren(SoftSlotDirective);

  protected readonly leadingSlot = computed(
    () => this.slots().find((s) => s.position() === 'leading') ?? null
  );
  protected readonly trailingSlot = computed(
    () => this.slots().find((s) => s.position() === 'trailing') ?? null
  );

  protected readonly hostClasses = computed(() => 'soft-ds-tab-host');

  protected readonly isActive = computed(() => this.host.value() === this.value());

  protected onClick(): void {
    if (this.disabled()) return;
    this.host.selectValue(this.value());
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (this.disabled()) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.host.selectValue(this.value());
    }
  }
}
